import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

interface DuesSummary {
  dojo_name: string;
  total_members: number;
  paid_count: number;
  unpaid_count: number;
  never_paid_count: number;
  collection_rate_pct: number;
  total_collected: number;
}

interface MemberDues {
  profile_id: number;
  full_name: string;
  dojo_name: string;
  membership_type: string;
  membership_start_date: string;
  membership_end_date: string;
  membership_status: string;
  amount_paid: number;
  last_payment_date: string;
  is_financially_current: number;
}

type ViewMode = 'summary' | 'detail';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 50;

const generateYears = (): number[] => {
  const currentYear = new Date().getFullYear();
  const startYear = 2017;
  const years: number[] = [];
  for (let y = currentYear + 1; y >= startYear; y--) {
    years.push(y);
  }
  return years;
};

const YEARS = generateYears();

const Dues: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('summary');
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [summary, setSummary] = useState<DuesSummary[]>([]);
  const [allDetail, setAllDetail] = useState<MemberDues[]>([]);
  const [filtered, setFiltered] = useState<MemberDues[]>([]);
  const [dojos, setDojos] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [sortField, setSortField] = useState<keyof MemberDues>('full_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedDojos, setSelectedDojos] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  const fetchSummary = useCallback(async (years: number[]) => {
    try {
      setLoadingSummary(true);
      setSummaryError('');
      const yearParam = years.length > 0 ? '?years=' + years.join(',') : '';
      const res = await api.get('/reports/memberships/dues-summary' + yearParam);
      setSummary(res.data.data as DuesSummary[]);
    } catch (err) {
      setSummaryError('Failed to load summary data — please try again');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchDetail = useCallback(async (years: number[]) => {
    try {
      setLoadingDetail(true);
      setDetailError('');
      const yearParam = years.length > 0 ? '&years=' + years.join(',') : '';
      const res = await api.get('/reports/memberships/status?limit=2000' + yearParam);
      const data = res.data.data as MemberDues[];
      setAllDetail(data);
      setFiltered(data);
      setTotalCount(data.length);
      setDojos(Array.from(new Set(data.map((m) => m.dojo_name))).sort());
    } catch (err) {
      setDetailError('Failed to load member detail — please try again');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary(selectedYears);
    fetchDetail(selectedYears);
  }, [selectedYears, fetchSummary, fetchDetail]);

  useEffect(() => {
    let result = allDetail;

    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (m) =>
          (m.full_name || '').toLowerCase().includes(s) ||
          (m.dojo_name || '').toLowerCase().includes(s) ||
          (m.membership_type || '').toLowerCase().includes(s)
      );
    }

    if (selectedDojos.length > 0) {
      result = result.filter((m) => selectedDojos.includes(m.dojo_name));
    }

    if (selectedStatuses.length > 0) {
      result = result.filter((m) => selectedStatuses.includes(m.membership_status));
    }

    result = [...result].sort((a, b) => {
      const av = a[sortField] !== undefined ? a[sortField] : '';
      const bv = b[sortField] !== undefined ? b[sortField] : '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

    setFiltered(result);
    setTotalCount(result.length);
    setPage(1);
  }, [search, selectedDojos, selectedStatuses, sortField, sortDir, allDetail]);

  const toggleSort = (field: keyof MemberDues) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field: keyof MemberDues) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const toggleMulti = (
    value: string,
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasFilters = search || selectedDojos.length > 0 || selectedStatuses.length > 0;

  const clearFilters = () => {
    setSearch('');
    setSelectedDojos([]);
    setSelectedStatuses([]);
    setPage(1);
  };

  const grandTotal = summary.reduce(
    (acc, d) => ({
      total_members: acc.total_members + Number(d.total_members),
      paid_count: acc.paid_count + Number(d.paid_count),
      unpaid_count: acc.unpaid_count + Number(d.unpaid_count),
      never_paid_count: acc.never_paid_count + Number(d.never_paid_count),
      total_collected: acc.total_collected + Number(d.total_collected),
    }),
    { total_members: 0, paid_count: 0, unpaid_count: 0, never_paid_count: 0, total_collected: 0 }
  );

  const exportExcel = () => {
    setExporting(true);
    try {
      const headers =
        viewMode === 'summary'
          ? ['Dojo', 'Total Members', 'Paid', 'Unpaid', 'Never Paid', 'Collection Rate %', 'Total Collected (₦)']
          : ['Full Name', 'Dojo', 'Membership Type', 'Start Date', 'End Date', 'Status', 'Amount Paid (₦)', 'Last Payment Date', 'Financially Current'];

      const rows =
        viewMode === 'summary'
          ? summary.map((d) => [
              d.dojo_name, d.total_members, d.paid_count, d.unpaid_count,
              d.never_paid_count, d.collection_rate_pct + '%', d.total_collected,
            ])
          : filtered.map((m) => [
              m.full_name, m.dojo_name, m.membership_type || '',
              m.membership_start_date ? new Date(m.membership_start_date).toLocaleDateString('en-GB') : '',
              m.membership_end_date ? new Date(m.membership_end_date).toLocaleDateString('en-GB') : '',
              m.membership_status, m.amount_paid || 0,
              m.last_payment_date ? new Date(m.last_payment_date).toLocaleDateString('en-GB') : '',
              m.is_financially_current ? 'Yes' : 'No',
            ]);

      const bom = '\uFEFF';
      const csv =
        bom +
        [headers, ...rows]
          .map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(','))
          .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        'NASK_Dues_' +
        viewMode +
        (selectedYears.length > 0 ? '_' + selectedYears.join('-') : '_AllYears') +
        '_' +
        new Date().toISOString().slice(0, 10) +
        '.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const yearLabel =
    selectedYears.length === 0
      ? 'All Years'
      : selectedYears.length === 1
      ? selectedYears[0].toString()
      : selectedYears.length + ' years';

  const MultiSelect: React.FC<{
    label: string;
    id: string;
    options: string[];
    selected: string[];
    setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  }> = ({ label, id, options, selected, setSelected }) => {
    const isOpen = openDropdown === id;
    return (
      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>{label}</label>
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : id); }}
            style={{ ...styles.multiSelectBtn, borderColor: selected.length > 0 ? '#cc0000' : '#3a3a3a' }}
          >
            <span>
              {selected.length === 0 ? 'All' : selected.length === 1 ? selected[0] : selected.length + ' selected'}
            </span>
            <span style={{ fontSize: '10px' }}>▼</span>
          </button>
          {isOpen && (
            <div style={styles.multiSelectDropdown} onClick={(e) => e.stopPropagation()}>
              {options.map((opt) => (
                <label key={opt} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={() => toggleMulti(opt, setSelected)}
                    style={{ marginRight: '8px', accentColor: '#cc0000' }}
                  />
                  {opt}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container} onClick={() => setOpenDropdown(null)}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Dues & Payments</h1>
          <p style={styles.subtitle}>
            {viewMode === 'summary'
              ? summary.length + ' dojos — ' + yearLabel
              : totalCount.toLocaleString() + ' members — ' + yearLabel}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          {/* Dues Expiry Year Multi-Select */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Dues Expiry Year</label>
            <div style={{ position: 'relative' }}>
              <button
                onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === 'year' ? null : 'year'); }}
                style={{ ...styles.multiSelectBtn, minWidth: '160px', borderColor: selectedYears.length > 0 ? '#cc0000' : '#3a3a3a' }}
              >
                <span>{yearLabel}</span>
                <span style={{ fontSize: '10px' }}>▼</span>
              </button>
              {openDropdown === 'year' && (
                <div
                  style={{ ...styles.multiSelectDropdown, minWidth: '160px', right: 0, left: 'auto' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {YEARS.map((y) => (
                    <label key={y} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={selectedYears.includes(y)}
                        onChange={() =>
                          setSelectedYears((prev) =>
                            prev.includes(y) ? prev.filter((v) => v !== y) : [...prev, y]
                          )
                        }
                        style={{ marginRight: '8px', accentColor: '#cc0000' }}
                      />
                      {y}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={exportExcel}
            style={exporting ? styles.exportBtnDisabled : styles.exportBtn}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : '⬇️ Export to Excel'}
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div style={styles.viewToggle}>
        <button
          onClick={() => setViewMode('summary')}
          style={viewMode === 'summary' ? styles.toggleBtnActive : styles.toggleBtn}
        >
          📊 Summary by Dojo
        </button>
        <button
          onClick={() => setViewMode('detail')}
          style={viewMode === 'detail' ? styles.toggleBtnActive : styles.toggleBtn}
        >
          👥 Member Detail
        </button>
      </div>

      {viewMode === 'summary' ? (
        loadingSummary ? (
          <div style={styles.loadingBox}>Loading summary data...</div>
        ) : summaryError ? (
          <div style={styles.errorBox}>
            {summaryError}
            <button onClick={() => fetchSummary(selectedYears)} style={styles.retryBtn}>Retry</button>
          </div>
        ) : (
          <>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Total Members</p>
                <p style={{ ...styles.statValue, color: '#3b82f6' }}>{grandTotal.total_members.toLocaleString()}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Dues Paid</p>
                <p style={{ ...styles.statValue, color: '#22c55e' }}>{grandTotal.paid_count.toLocaleString()}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Dues Unpaid</p>
                <p style={{ ...styles.statValue, color: '#f59e0b' }}>{grandTotal.unpaid_count.toLocaleString()}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Never Paid</p>
                <p style={{ ...styles.statValue, color: '#cc0000' }}>{grandTotal.never_paid_count.toLocaleString()}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Collection Rate</p>
                <p style={{ ...styles.statValue, color: '#6b8cff' }}>
                  {grandTotal.total_members > 0
                    ? Math.round((grandTotal.paid_count / grandTotal.total_members) * 100) + '%'
                    : '0%'}
                </p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Total Collected</p>
                <p style={{ ...styles.statValue, color: '#22c55e' }}>
                  {'₦' + grandTotal.total_collected.toLocaleString()}
                </p>
              </div>
            </div>

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Dojo', 'Total Members', 'Paid', 'Unpaid', 'Never Paid', 'Collection Rate', 'Total Collected'].map((h) => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summary.map((d, i) => (
                    <tr key={d.dojo_name} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={{ ...styles.td, fontWeight: '600', color: '#ffffff' }}>{d.dojo_name}</td>
                      <td style={styles.tdNum}>{Number(d.total_members).toLocaleString()}</td>
                      <td style={{ ...styles.tdNum, color: '#22c55e' }}>{Number(d.paid_count).toLocaleString()}</td>
                      <td style={{ ...styles.tdNum, color: '#f59e0b' }}>{Number(d.unpaid_count).toLocaleString()}</td>
                      <td style={{ ...styles.tdNum, color: '#cc0000' }}>{Number(d.never_paid_count).toLocaleString()}</td>
                      <td style={styles.tdNum}>
                        <div style={styles.progressBar}>
                          <div
                            style={{
                              ...styles.progressFill,
                              width: d.collection_rate_pct + '%',
                              backgroundColor:
                                Number(d.collection_rate_pct) >= 70
                                  ? '#22c55e'
                                  : Number(d.collection_rate_pct) >= 40
                                  ? '#f59e0b'
                                  : '#cc0000',
                            }}
                          />
                          <span style={styles.progressLabel}>{d.collection_rate_pct}%</span>
                        </div>
                      </td>
                      <td style={{ ...styles.tdNum, color: '#22c55e' }}>
                        {'₦' + Number(d.total_collected).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={styles.footerRow}>
                    <td style={{ ...styles.td, fontWeight: '700', color: '#ffffff' }}>TOTAL</td>
                    <td style={styles.tdNum}>{grandTotal.total_members.toLocaleString()}</td>
                    <td style={{ ...styles.tdNum, color: '#22c55e' }}>{grandTotal.paid_count.toLocaleString()}</td>
                    <td style={{ ...styles.tdNum, color: '#f59e0b' }}>{grandTotal.unpaid_count.toLocaleString()}</td>
                    <td style={{ ...styles.tdNum, color: '#cc0000' }}>{grandTotal.never_paid_count.toLocaleString()}</td>
                    <td style={styles.tdNum}>
                      {grandTotal.total_members > 0
                        ? Math.round((grandTotal.paid_count / grandTotal.total_members) * 100) + '%'
                        : '0%'}
                    </td>
                    <td style={{ ...styles.tdNum, color: '#22c55e' }}>
                      {'₦' + grandTotal.total_collected.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )
      ) : loadingDetail ? (
        <div style={styles.loadingBox}>Loading member detail...</div>
      ) : detailError ? (
        <div style={styles.errorBox}>
          {detailError}
          <button onClick={() => fetchDetail(selectedYears)} style={styles.retryBtn}>Retry</button>
        </div>
      ) : (
        <>
          <div style={styles.filtersCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.filtersRow}>
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Search</label>
                <input
                  type="text"
                  placeholder="Name, dojo, membership type..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={styles.searchInput}
                />
              </div>
              <MultiSelect label="Dojo" id="dojo" options={dojos} selected={selectedDojos} setSelected={setSelectedDojos} />
              <MultiSelect
                label="Status"
                id="status"
                options={['Active', 'Expired', 'Never Paid']}
                selected={selectedStatuses}
                setSelected={setSelectedStatuses}
              />
              {hasFilters && (
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>&nbsp;</label>
                  <button onClick={clearFilters} style={styles.clearBtn}>Clear All</button>
                </div>
              )}
            </div>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {[
                    { label: 'Full Name', field: 'full_name' },
                    { label: 'Dojo', field: 'dojo_name' },
                    { label: 'Membership Type', field: 'membership_type' },
                    { label: 'Start Date', field: 'membership_start_date' },
                    { label: 'End Date', field: 'membership_end_date' },
                    { label: 'Status', field: 'membership_status' },
                    { label: 'Amount Paid', field: 'amount_paid' },
                    { label: 'Last Payment', field: 'last_payment_date' },
                  ].map((col) => (
                    <th
                      key={col.field}
                      style={styles.th}
                      onClick={() => toggleSort(col.field as keyof MemberDues)}
                    >
                      {col.label}
                      <span style={styles.sortIcon}>{sortIcon(col.field as keyof MemberDues)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((m, i) => (
                  <tr key={m.profile_id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={{ ...styles.td, fontWeight: '500', color: '#ffffff' }}>{m.full_name}</td>
                    <td style={styles.td}>{m.dojo_name}</td>
                    <td style={styles.td}>{m.membership_type || '—'}</td>
                    <td style={styles.td}>
                      {m.membership_start_date ? new Date(m.membership_start_date).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td style={styles.td}>
                      {m.membership_end_date ? new Date(m.membership_end_date).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={
                          m.membership_status === 'Active'
                            ? styles.statusActive
                            : m.membership_status === 'Never Paid'
                            ? styles.statusNever
                            : styles.statusExpired
                        }
                      >
                        {m.membership_status}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      {m.amount_paid ? '₦' + Number(m.amount_paid).toLocaleString() : '—'}
                    </td>
                    <td style={styles.td}>
                      {m.last_payment_date ? new Date(m.last_payment_date).toLocaleDateString('en-GB') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {paginated.length === 0 && (
              <div style={styles.emptyBox}>No members found matching your filters.</div>
            )}
          </div>

          {totalCount > PAGE_SIZE && (
            <div style={styles.pagination}>
              <span style={styles.pageInfo}>
                {'Showing ' +
                  ((page - 1) * PAGE_SIZE + 1).toLocaleString() +
                  '–' +
                  Math.min(page * PAGE_SIZE, totalCount).toLocaleString() +
                  ' of ' +
                  totalCount.toLocaleString() +
                  ' members'}
              </span>
              <div style={styles.pageButtons}>
                <button onClick={() => setPage(1)} disabled={page === 1} style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}>First</button>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}>Prev</button>
                <span style={styles.pageNumber}>{'Page ' + page + ' of ' + totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}>Next</button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}>Last</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '32px', fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' },
  title: { color: '#ffffff', fontSize: '24px', fontWeight: '700', margin: '0 0 4px 0' },
  subtitle: { color: '#888888', fontSize: '14px', margin: '0' },
  exportBtn: { backgroundColor: '#1a3a1a', border: '1px solid #22c55e', color: '#22c55e', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
  exportBtnDisabled: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#666666', padding: '10px 20px', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px' },
  viewToggle: { display: 'flex', gap: '8px', marginBottom: '24px' },
  toggleBtn: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#888888', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  toggleBtnActive: { backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#ffffff', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px' },
  statLabel: { color: '#888888', fontSize: '12px', margin: '0 0 8px 0', fontWeight: '500' },
  statValue: { fontSize: '28px', fontWeight: '700', margin: '0' },
  retryBtn: { marginLeft: '12px', backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#cc0000', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  errorBox: { backgroundColor: '#2d1a1a', border: '1px solid #cc3333', color: '#ff6666', padding: '12px 16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center' },
  loadingBox: { color: '#888888', textAlign: 'center', padding: '48px' },
  tableWrapper: { overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 360px)', borderRadius: '12px', border: '1px solid #2a2a2a', marginBottom: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { backgroundColor: '#1a1a1a', color: '#888888', padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1, cursor: 'pointer', userSelect: 'none' },
  sortIcon: { color: '#cc0000', fontSize: '10px' },
  trEven: { backgroundColor: '#0f0f0f' },
  trOdd: { backgroundColor: '#111111' },
  td: { padding: '12px 16px', color: '#cccccc', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap' },
  tdNum: { padding: '12px 16px', color: '#cccccc', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap', textAlign: 'right' },
  footerRow: { backgroundColor: '#1a1a1a', borderTop: '2px solid #3a3a3a' },
  progressBar: { position: 'relative', backgroundColor: '#2a2a2a', borderRadius: '4px', height: '20px', minWidth: '100px', overflow: 'hidden' },
  progressFill: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: '4px', transition: 'width 0.3s ease' },
  progressLabel: { position: 'absolute', right: '6px', top: '2px', fontSize: '11px', fontWeight: '600', color: '#ffffff', zIndex: 1 },
  statusActive: { backgroundColor: '#0d2d1a', color: '#22c55e', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusExpired: { backgroundColor: '#2d1a0d', color: '#f59e0b', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusNever: { backgroundColor: '#2d1a1a', color: '#cc0000', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  emptyBox: { color: '#888888', textAlign: 'center', padding: '48px', backgroundColor: '#0f0f0f' },
  filtersCard: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px', marginBottom: '24px' },
  filtersRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' },
  filterLabel: { color: '#888888', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  searchInput: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', outline: 'none', minWidth: '220px' },
  multiSelectBtn: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', cursor: 'pointer', textAlign: 'left', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  multiSelectDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '8px', zIndex: 100, maxHeight: '200px', overflowY: 'auto', marginTop: '4px', minWidth: '180px' },
  checkboxLabel: { display: 'flex', alignItems: 'center', color: '#cccccc', fontSize: '13px', padding: '6px 8px', cursor: 'pointer', borderRadius: '4px' },
  clearBtn: { backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#cc0000', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { color: '#888888', fontSize: '14px' },
  pageButtons: { display: 'flex', gap: '8px', alignItems: 'center' },
  pageBtn: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#ffffff', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  pageBtnDisabled: { backgroundColor: '#111111', border: '1px solid #222222', color: '#444444', padding: '8px 14px', borderRadius: '6px', cursor: 'not-allowed', fontSize: '13px' },
  pageNumber: { color: '#888888', fontSize: '13px', padding: '0 8px' },
};

export default Dues;